import React, { useContext, useState } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { AuthContext } from '../context/AuthContext';
import {
  authErrorMessage,
  continueWithGoogle,
  ensureAccountProfile,
} from '../services/auth';
import { requestPasswordReset } from '../services/passwordReset';
import AuthShell, { GoogleIcon } from './AuthShell';
import Header from './Header';
import Footer from './Footer';
import '../style/Auth.css';
import { useI18n } from '../i18n/I18nContext';

const Login = () => {
  const { setIsLoggedIn, setUserType } = useContext(AuthContext);
  const { t, language } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const completeLogin = (role, approvalStatus) => {
    setUserType(role);
    setIsLoggedIn(true);
    if (role === 'admin') {
      navigate('/admin-login');
      return;
    }
    if (role === 'teller') {
      navigate(approvalStatus === 'approved' ? '/teller-dashboard' : '/onboarding/teller');
      return;
    }
    navigate('/user-dashboard');
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading('email');
    setError('');
    setMessage('');
    try {
      const credential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const account = await ensureAccountProfile(credential.user);
      completeLogin(account.role, account.approvalStatus);
    } catch (loginError) {
      setError(authErrorMessage(loginError, language));
    } finally {
      setLoading('');
    }
  };

  const handleGoogleLogin = async () => {
    setLoading('google');
    setError('');
    setMessage('');
    try {
      const account = await continueWithGoogle('user');
      completeLogin(account.role, account.approvalStatus);
    } catch (loginError) {
      setError(authErrorMessage(loginError, language));
    } finally {
      setLoading('');
    }
  };

  const handleResetPassword = async () => {
    if (!email.trim()) {
      setError(t('auth.resetNeedEmail'));
      return;
    }
    setLoading('reset');
    setError('');
    setMessage('');
    try {
      await requestPasswordReset(email);
      setMessage(t('auth.resetSent'));
    } catch (resetError) {
      setError(authErrorMessage(resetError, language));
    } finally {
      setLoading('');
    }
  };

  return (
    <div>
      <Header />
      <AuthShell
        eyebrow={t('auth.loginEyebrow')}
        title={t('auth.loginTitle')}
        description={t('auth.loginDescription')}
        storyTitle={t('auth.loginStory')}
        storyBody={t('auth.loginStoryBody')}
        alternateText={t('auth.new')}
        alternateLabel={t('auth.create')}
        alternatePath="/register"
      >
        <button
          type="button"
          className="google-auth-button"
          onClick={handleGoogleLogin}
          disabled={Boolean(loading)}
        >
          <GoogleIcon />
          {loading === 'google' ? t('auth.googleConnecting') : t('auth.googleLogin')}
        </button>

        <div className="auth-divider"><span>{t('auth.orEmail')}</span></div>

        {(error || message) && (
          <div className={error ? 'auth-feedback error' : 'auth-feedback success'} role={error ? 'alert' : 'status'}>
            {error || message}
          </div>
        )}

        <form className="auth-form" onSubmit={handleLogin}>
          <label>
            {t('auth.email')}
            <input
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@example.com"
              autoComplete="username"
            />
          </label>
          <label>
            <span className="auth-label-row">
              <span>{t('auth.password')}</span>
              <button type="button" onClick={handleResetPassword} disabled={Boolean(loading)}>
                {t('auth.forgot')}
              </button>
            </span>
            <span className="password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="current-password"
                placeholder={t('auth.passwordPlaceholder')}
              />
              <button type="button" onClick={() => setShowPassword((current) => !current)}>
                {showPassword ? t('auth.hide') : t('auth.show')}
              </button>
            </span>
          </label>
          <button className="auth-submit" type="submit" disabled={Boolean(loading)}>
            {loading === 'email' ? t('auth.opening') : t('auth.login')}
          </button>
        </form>
      </AuthShell>
      <Footer />
    </div>
  );
};

export default Login;
